import {SELF_SUPPORTING_WIDE_WIDTH, SMALL_PLATE_HEIGHT, STANDARD_PLATE_HEIGHT} from 'App';
import {HINGES_180, HINGES_STANDARD} from 'fenceTypesConstants';
import { parse } from 'uuid';

export const calculateGateStandardPlates = ({values, elements, areasAmount}) => {
  const stdPlates = elements.filter((el) => el === STANDARD_PLATE_HEIGHT).length;
  return stdPlates * areasAmount;
}

export const calculateGateSmallPlates = ({values, elements, areasAmount}) => {
  return elements.filter((el) => el === SMALL_PLATE_HEIGHT && el !== values.gate.moduleHeight).length * areasAmount
}

export const calculateGateNonStandardPlates = ({values, elements, areasAmount, moduleHeight}) => {
  const nstdPlates = elements.filter((el) => el !== STANDARD_PLATE_HEIGHT && el !== SMALL_PLATE_HEIGHT && el !== moduleHeight).length;
  return nstdPlates * areasAmount;
}

// Height in mm of the field, i.e. what is left of the gate height once the frame
// and the mounting clearances are taken off. Mirrors _gate_height in
// kns-excel xps_calculations/calculations.py:761-778.
export const calculateGateAreaHight = ({height, type, width}) => {
  if (type === 'gateSelfSupporting') {
    // 60 frame + 80 top clearance + 80 bottom clearance + 2 plate thickness, and
    // 80 + 80 + 120 + 1 for the wide variant.
    return width <= SELF_SUPPORTING_WIDE_WIDTH ? height - 222 : height - 281;
  }
  return height - 120;
}

// Matches Python's round() (banker's / half-to-even), which
// kns-excel xps_calculations/calculations.py:440-447 applies to every gate area
// width. Gate widths are snapped to 10 mm, so .5 results are routine.
const roundHalfEven = (n) => {
  if (!Number.isFinite(n)) return n;
  const floor = Math.floor(n);
  if (n - floor !== 0.5) return Math.round(n);
  return floor % 2 === 0 ? floor : floor + 1;
};

const rawGateAreaWidth = ({width, hinges, type, mountingStrip}) => {
  if (type === 'gateTilting') {
    if (width <= 4999) {
      if (hinges === HINGES_STANDARD) {
        return Math.round(((width - 370) / 2) * 100) / 100;
      } else if (hinges === HINGES_180) {
        return Math.round(((width - 270) / 2) * 100) / 100;
      } else {
        throw new Error('c55d50bc-8438-4e41-94e9-6c8b16ffd0cc: unsupported hinges');
      }
    }
    if (width <= 5999) {
      if (hinges === HINGES_STANDARD) {
        return Math.round(((width - 490) / 4) * 100) / 100;
      } else if (hinges === HINGES_180) {
        return Math.round(((width - 390) / 4) * 100) / 100;
      } else {
        throw new Error('2918600c-6bec-4ce5-bb8d-5dfe94ad9281: unsupported hinges');
      }
    } else {
      if (hinges === HINGES_STANDARD) {
        return Math.round(((width - 640) / 4) * 100) / 100;
      } else if (hinges === HINGES_180) {
        return Math.round(((width - 560) / 4) * 100) / 100;
      } else {
        throw new Error('2918600c-6bec-4ce5-bb8d-5dfe94ad9281: unsupported hinges');
      }
    }
  } else if (type === 'gateSelfSupporting') {
    if (width <= 2000) {
      return (width + 100) - 120;
    } else if (width <= 4499) {
      return Math.round(((width + 100) - 180) / 2);
    } else if (width <= 5800) {
      return Math.round(((width + 100) - 240) / 3);
    } else if (width <= 6499) {
      return Math.round((width - 220) / 3);
    } else if (width <= 7500) {
      return Math.round((width - 300) / 4);
    } else {
      throw new Error('5af0936d-8dd0-4040-97b4-1be461ef1c08: unsupported width');
    }
  } else if (type === 'singleLeafGate') {
    if (hinges === HINGES_STANDARD) {
      if (width <= 2499) {
        return width - 235;
      } else if (width <= 2999) {
        return (width - 295) / 2;
      } else {
        return (width - 370) / 2;
      }
    } else if (hinges === HINGES_180) {
      if (width <= 2499) {
        return width - 185;
      } else if (width <= 2999) {
        return (width - 245) / 2;
      } else {
        return (width - 330) / 2; 
      }
    } else {
      throw new Error('fce2801f-60dc-4694-9233-e2ef9a270edd: unsupported hinges');
    }
  } else if (type === 'foldingGate') {
      if (mountingStrip === '0') {
        return (width - 660) / 4;
      } else if (mountingStrip === '1') {
        return (width - 740) / 4;
      } else if (mountingStrip === '2') {
        return (width - 820) / 4;
      } else {
        throw new Error('d44be119-bae6-449a-8cd2-ea4eeb1df236: unsupported mountingStrip');
      }
  } else if (type === 'singleLeafFoldingGate') {
      if (mountingStrip === '0') {
        return (width - 460) / 2;
      } else {
        return (width - 540) / 2;
      }
  } else if (type === 'nonSymmetricalGate') {
  if (width <= 2499) {
    if (hinges === HINGES_STANDARD) {
      return width - 225;
    } else if (hinges === HINGES_180) {
      return width - 175;
    }
  } else if (width <= 2999) {
    if (hinges === HINGES_STANDARD) {
      return (width - 305) / 2;
    } else if (hinges === HINGES_180) {
      return (width - 255) / 2;
    }
  } else {
    if (hinges === HINGES_STANDARD) {
      return (width - 320) / 2
    } else if (hinges === HINGES_180) {
      return (width - 280) / 2;
    }
  }
  } else {
    throw new Error('0f285f45-7c14-4120-88f1-17520d6933cc: unsupported gate type');
  }
}

export const calculateGateAreaWidth = (params) => roundHalfEven(rawGateAreaWidth(params));

export const calculateGateAreasAmount = ({type, width}) => {
  if (type === 'foldingGate') {
    return 4;
  } else if (type === 'singleLeafFoldingGate') {
    return 2;
  } else if (type === 'gateTilting') {
    return width <= 4999 ? 2 : 4;
  } else if (type === 'singleLeafGate') {
    if (width <= 2499) {
      return 1;
    } else if (width <= 4999) {
      return 2;
    } else {
      return 4;
    }
  } else if (type === 'gateSelfSupporting') {
    if (width <= 2000) {
      return 1;
    } else if (width <= 4499) {
      return 2;
    } else if (width <= 6499) {
      return 3
    } else if (width <= 7500) {
      return 4
    } else {
      return 4;
    }
  } else if (type === 'nonSymmetricalGate') {
    return (width <= 2499) ? 1 : 2;
  } else {
    throw new Error('0a4635b3-5dea-4b28-b1e2-04ac40e59f15: unsupported gate type');
  }
}

// Two ceowniki per plate. The plate counts already carry the areas multiplier,
// so this is Python's 2 * rods * multiplier * amount
// (kns-excel xps_calculations/calculations.py, prepare_response).
export const calculateGateRodsAmount = ({standardPlates, nonStandardPlates, smallPlates, amount}) => {
  return (standardPlates + nonStandardPlates + smallPlates) * 2 * amount;
}
